import { DayPicker } from "@daypicker/react";
import { CalendarDays, ChevronDown, ChevronUp, Clock3, Minus, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { detailedEventDistance } from "../../../shared/event-time";
import { zonedInputToIso, zonedInputValue } from "../../composer/timezones";

type TimeFormat = "24" | "12";

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function eventTimeSummary(startsAt: string, timezone: string) {
  const date = new Date(startsAt);
  if (!Number.isFinite(date.getTime())) return "Choose a valid time";
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  }).format(date);
  const time24 = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: timezone,
  }).format(date);
  const time12 = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h12",
    timeZone: timezone,
  }).format(date);
  return `${day} · ${time24} (${time12})`;
}

export function RsvpDateTimePicker({
  startsAt,
  timezone,
  format,
  onChange,
  onFormatChange,
}: {
  startsAt: string;
  timezone: string;
  format: TimeFormat;
  onChange: (startsAt: string) => void;
  onFormatChange: (format: TimeFormat) => void;
}) {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);
  const local = zonedInputValue(startsAt, timezone);
  const [dateValue = "", timeValue = "00:00"] = local.split("T");
  const [hourText = "00", minuteText = "00"] = timeValue.split(":");
  const hour24 = Number(hourText);
  const minute = Number(minuteText);
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  const selectedDate = useMemo(() => {
    const [year, month, day] = dateValue.split("-").map(Number);
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day, 12);
  }, [dateValue]);
  const apply = (date: string, hour: number, selectedMinute: number) => {
    if (!date) return;
    onChange(zonedInputToIso(`${date}T${pad(hour)}:${pad(selectedMinute)}`, timezone));
  };
  const adjustHour = (amount: number) => apply(dateValue, (hour24 + amount + 24) % 24, minute);
  const adjustMinute = (amount: number) => {
    const next = (minute + amount + 60) % 60;
    apply(dateValue, hour24, next);
  };
  const setPeriod = (nextPeriod: "AM" | "PM") => {
    apply(dateValue, (hour12 % 12) + (nextPeriod === "PM" ? 12 : 0), minute);
  };
  const selectDate = (date: Date) => {
    apply(`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`, hour24, minute);
  };
  const todayValue = zonedInputValue(new Date().toISOString(), timezone).split("T")[0] ?? dateValue;
  const summary = eventTimeSummary(startsAt, timezone);
  return (
    <div className="rsvp-datetime-picker">
      <span className="rsvp-datetime-label">Date and time</span>
      <button
        className="rsvp-datetime-trigger"
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <CalendarDays size={15} aria-hidden="true" />
        <span>
          <strong>{summary}</strong>
          <small>{detailedEventDistance(startsAt, now)}</small>
        </span>
        {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
      </button>
      {open && (
        <>
          <button
            className="rsvp-datetime-backdrop"
            type="button"
            aria-label="Close date picker"
            onClick={() => setOpen(false)}
          />
          <div className="rsvp-datetime-popover" role="dialog" aria-label="Choose event date and time">
            <div className="rsvp-datetime-popover-head">
              <div>
                <strong>When is it?</strong>
                <span>{timezone}</span>
              </div>
              <button type="button" aria-label="Close date picker" onClick={() => setOpen(false)}>
                <X size={15} aria-hidden="true" />
              </button>
            </div>
            <DayPicker
              animate
              fixedWeeks
              mode="single"
              required
              weekStartsOn={1}
              selected={selectedDate}
              defaultMonth={selectedDate}
              onSelect={selectDate}
            />
            <div className="rsvp-time-panel">
              <div className="rsvp-time-panel-head">
                <span>
                  <Clock3 size={14} aria-hidden="true" /> Time
                </span>
                <div className="rsvp-clock-format">
                  <button type="button" aria-pressed={format === "24"} onClick={() => onFormatChange("24")}>
                    24h
                  </button>
                  <button type="button" aria-pressed={format === "12"} onClick={() => onFormatChange("12")}>
                    12h
                  </button>
                </div>
              </div>
              <div className="rsvp-time-stepper-row">
                <div className="rsvp-time-stepper">
                  <button type="button" aria-label="One hour earlier" onClick={() => adjustHour(-1)}>
                    <Minus size={14} aria-hidden="true" />
                  </button>
                  <output aria-label="Event hour">{format === "24" ? pad(hour24) : pad(hour12)}</output>
                  <button type="button" aria-label="One hour later" onClick={() => adjustHour(1)}>
                    <Plus size={14} aria-hidden="true" />
                  </button>
                </div>
                <b>:</b>
                <div className="rsvp-time-stepper">
                  <button type="button" aria-label="Five minutes earlier" onClick={() => adjustMinute(-5)}>
                    <Minus size={14} aria-hidden="true" />
                  </button>
                  <output aria-label="Event minute">{pad(minute)}</output>
                  <button type="button" aria-label="Five minutes later" onClick={() => adjustMinute(5)}>
                    <Plus size={14} aria-hidden="true" />
                  </button>
                </div>
                {format === "12" && (
                  <div className="rsvp-period-toggle">
                    <button type="button" aria-pressed={period === "AM"} onClick={() => setPeriod("AM")}>
                      AM
                    </button>
                    <button type="button" aria-pressed={period === "PM"} onClick={() => setPeriod("PM")}>
                      PM
                    </button>
                  </div>
                )}
              </div>
              <fieldset className="rsvp-minute-shortcuts">
                <legend className="visually-hidden">Quick minute selection</legend>
                {[0, 15, 30, 45].map((value) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={minute === value}
                    onClick={() => apply(dateValue, hour24, value)}
                  >
                    :{pad(value)}
                  </button>
                ))}
              </fieldset>
            </div>
            <div className="rsvp-datetime-popover-foot">
              <button type="button" onClick={() => apply(todayValue, hour24, minute)}>
                Today
              </button>
              <span>{detailedEventDistance(startsAt, now)}</span>
              <button type="button" className="primary" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
